import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Loader2, Filter, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";

const LAYERS = [
  { id: "",           label: "Toutes" },
  { id: "universal",  label: "Universelle" },
  { id: "profession", label: "Métier" },
  { id: "doctrine",   label: "Doctrine ALTIORA" },
];

const EVIDENCE_META = {
  A: { color: "#0B7A3B", label: "Consensus solide" },
  B: { color: "#7A5B14", label: "Reconnue" },
  C: { color: "#B45309", label: "Bonnes pratiques" },
  D: { color: "#7C3AED", label: "En expérimentation" },
};

export default function AbiKnowledge() {
  const [cards, setCards] = useState([]);
  const [layer, setLayer] = useState("");
  const [q, setQ] = useState("");
  const [ev, setEv] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (layer) params.set("layer", layer);
      if (q)     params.set("q", q);
      if (ev)    params.set("evidence_level", ev);
      const r = await api.get(`/abi/copilot/knowledge?${params.toString()}`);
      setCards(r.data.cards || []);
    } catch { toast.error("Erreur de chargement."); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [layer, q, ev]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FBF6EA" }}
          data-testid="abi-knowledge-root">
      <TopBar />

      {/* Hero */}
      <div className="px-8 pt-8 pb-6"
            style={{ background: "linear-gradient(120deg,#0B2545 0%,#123B6E 60%,#1D5AA5 100%)" }}>
        <div className="max-w-6xl mx-auto text-ivory">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest opacity-80">
            <BookOpen size={14} /> ALTIORA · Professional Knowledge Engine
          </div>
          <h1 className="text-3xl md:text-4xl font-serif mt-2 leading-tight text-white"
              data-testid="abi-knowledge-title">
            Bibliothèque scientifique · le cerveau d'ABI
          </h1>
          <p className="text-white/85 mt-3 max-w-3xl italic">
            Chaque recommandation du Copilote est retraçable jusqu'à une fiche
            versionnée. Aucun conseil qui ne repose sur des connaissances
            reconnues, des données réelles ou une hypothèse ALTIORA identifiée.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-8 pt-6 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className="text-slate-500" />
          {LAYERS.map(l => (
            <button key={l.id} onClick={() => setLayer(l.id)}
                    data-testid={`abi-knowledge-filter-${l.id || "all"}`}
                    className="text-[12px] px-3 py-1.5 rounded-full font-semibold"
                    style={{
                      backgroundColor: layer === l.id ? "#0B2545" : "#fff",
                      color: layer === l.id ? "#C5A880" : "#0B2545",
                      border: "1px solid #E5E7EB",
                    }}>
              {l.label}
            </button>
          ))}
          <span className="ml-auto text-[12px] text-slate-500">
            {cards.length} fiche(s)
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="text" value={q} onChange={e => setQ(e.target.value)}
                  placeholder="Rechercher (auteur · principe · référence)…"
                  className="flex-1 border rounded px-3 py-1.5 text-[13px]"
                  data-testid="abi-knowledge-search"
                  style={{ borderColor: "#E5E7EB" }} />
          {["", "A", "B", "C", "D"].map(v => (
            <button key={v||"any"} onClick={() => setEv(v)}
                    className="text-[11px] px-2 py-1 rounded font-mono"
                    data-testid={`abi-knowledge-ev-${v||"any"}`}
                    style={{
                      backgroundColor: ev === v ? "#0B2545" : "#F3F4F6",
                      color: ev === v ? "#C5A880" : "#5B5B62",
                    }}>
              {v ? `Preuve ${v}` : "Toutes preuves"}
            </button>
          ))}
        </div>
      </div>

      {/* Cards grid */}
      <div className="max-w-6xl mx-auto px-8 py-6 grid md:grid-cols-2 gap-3">
        {loading && (
          <div className="col-span-2 flex items-center gap-2 p-4">
            <Loader2 size={16} className="animate-spin" /> Chargement…
          </div>
        )}
        {cards.map(c => {
          const m = EVIDENCE_META[c.evidence_level] || EVIDENCE_META.D;
          return (
            <button key={c.card_id}
                    onClick={() => setSelected(c)}
                    className="text-left bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition"
                    style={{ borderLeft: `4px solid ${m.color}` }}
                    data-testid={`abi-knowledge-card-${c.card_id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-widest font-bold"
                        style={{ color: "#7A5B14" }}>
                  {c.layer === "universal" ? "Universelle" :
                   c.layer === "profession" ? "Métier" : "Doctrine"}
                </span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded"
                        style={{ backgroundColor: m.color, color: "#fff" }}>
                  Preuve {c.evidence_level}
                </span>
              </div>
              <div className="font-serif text-lg" style={{ color: "#0B2545" }}>
                {c.title_fr}
              </div>
              <div className="text-[13px] text-slate-700 mt-1 line-clamp-2">
                {c.principle_fr}
              </div>
              <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-500">
                <BookOpen size={11} /> {(c.references_fr || []).slice(0,1).join("")}
              </div>
            </button>
          );
        })}
      </div>

      {/* Modal — detailed card */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={() => setSelected(null)}
              data-testid="abi-knowledge-modal">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b" style={{
                  background: "linear-gradient(120deg,#0B2545 0%,#123B6E 100%)",
                  borderColor: "#E5E7EB" }}>
              <div className="text-white flex items-start gap-3">
                <ShieldCheck size={24} />
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-80">
                    Fiche APKE · version {selected.version} · Preuve {selected.evidence_level}
                  </div>
                  <h2 className="font-serif text-2xl mt-0.5">{selected.title_fr}</h2>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4 text-[14px]">
              <FieldBlock label="Principe" tone="#0B2545" text={selected.principle_fr} tid="fld-principle" />
              <FieldBlock label="Comportement attendu" tone="#7A5B14" text={selected.expected_behavior_fr} tid="fld-behavior" />
              <FieldBlock label="Contexte" tone="#0B2545" text={selected.context_fr} tid="fld-context" />
              <div className="grid md:grid-cols-2 gap-3">
                <FieldBlock label="Quand appliquer" tone="#0B7A3B" text={selected.when_to_apply_fr} tid="fld-when" />
                <FieldBlock label="Quand NE PAS appliquer" tone="#B91C1C" text={selected.when_NOT_to_apply_fr} tid="fld-when-not" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <FieldBlock label="Bénéfices attendus" tone="#0B7A3B" text={selected.benefits_fr} tid="fld-benefits" />
                <FieldBlock label="Risques éventuels" tone="#B91C1C" text={selected.risks_fr} tid="fld-risks" />
              </div>
              <ListBlock label="Indicateurs d'efficacité" tone="#7C3AED" items={selected.effectiveness_indicators_fr} tid="fld-indicators" />
              <ListBlock label="Références" tone="#7A5B14" items={selected.references_fr} tid="fld-references" />
              <div className="pt-3 border-t text-[11px] text-slate-500 italic"
                    style={{ borderTopColor: "#E5E7EB" }}>
                Fiche liée à la compétence · {selected.competency}
              </div>
            </div>
            <div className="p-4 border-t flex items-center justify-between gap-3"
                  style={{ borderTopColor: "#E5E7EB" }}>
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-slate-500 mr-2">Cette fiche vous a-t-elle aidé ?</span>
                {[
                  { v: "helpful",     lbl: "Oui",         bg: "#DCFCE7", fg: "#0B7A3B" },
                  { v: "partial",     lbl: "Partiellement", bg: "#FEF3C7", fg: "#B45309" },
                  { v: "not_helpful", lbl: "Non",         bg: "#FEE2E2", fg: "#B91C1C" },
                ].map(o => (
                  <button key={o.v}
                          onClick={async () => {
                            try {
                              await api.post(`/abi/copilot/knowledge/${selected.card_id}/feedback`,
                                              { card_id: selected.card_id, verdict: o.v });
                              toast.success("Merci — ABI ajuste vos recommandations.");
                            } catch { toast.error("Erreur"); }
                          }}
                          data-testid={`abi-knowledge-feedback-${o.v}`}
                          className="text-[11px] px-2 py-1 rounded"
                          style={{ backgroundColor: o.bg, color: o.fg }}>
                    {o.lbl}
                  </button>
                ))}
              </div>
              <button onClick={() => setSelected(null)}
                      className="px-4 py-1.5 rounded text-[13px]"
                      style={{ backgroundColor: "#0B2545", color: "#fff" }}
                      data-testid="abi-knowledge-modal-close">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldBlock({ label, tone, text, tid }) {
  return (
    <div data-testid={tid}>
      <div className="text-[10px] uppercase tracking-widest font-bold mb-1"
            style={{ color: tone }}>
        {label}
      </div>
      <div className="text-slate-800 leading-relaxed">{text}</div>
    </div>
  );
}

function ListBlock({ label, tone, items, tid }) {
  return (
    <div data-testid={tid}>
      <div className="text-[10px] uppercase tracking-widest font-bold mb-1"
            style={{ color: tone }}>
        {label}
      </div>
      <ul className="space-y-1">
        {(items || []).map((it, i) => (
          <li key={i} className="text-slate-800">· {it}</li>
        ))}
      </ul>
    </div>
  );
}
